import React, { useState } from 'react';

import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { toast } from 'react-toastify';

import { PAGES } from '../../constants/pages';
import { AiOutlineInfoCircle } from 'react-icons/ai';

import { MicrostoreElementState, useCreateColumnInventoryStockMutation, TotadminPermission } from 'data/__generated__';

import { Footer } from 'components/Footer';
import { Header, HeaderTitle } from 'components/Header';
import { TotemPrimaryButton } from 'components/TotemPrimaryButton';
import { TotemInput } from 'components/TotemInput';
import { PageTitle } from 'components/PageTitle';
import { Card, CardContent } from 'components/ui/card';
import { ColumnSelect } from 'components/Select/ColumnSelect';
import { ProductWithStocksSelector } from './components/ProductWithStocksSelector';
import { PermissionGate } from 'components/PermissionGate';

type FormType = {
    columnId: string | null;
    productId: string | null;
    productLabel: string | null;
    quantity: number | null;
    explanation: string | null;
};

const initialFormData: FormType = {
    columnId: null,
    productId: null,
    productLabel: null,
    quantity: null,
    explanation: null,
};

const SELECTABLE_COLUMN_STATES = [MicrostoreElementState.Regular];

export const SiteInventoryStockCreate = () => {
    const [formData, setFormData] = useState<FormType>(initialFormData);
    const [createSiteInventoryStock] = useCreateColumnInventoryStockMutation();

    const handleColumnSelect = (columnId: string | null) => {
        setFormData({ ...formData, columnId, productId: null, productLabel: null });
    };

    const handleProductSelect = (productId: string, productLabel: string) => {
        setFormData({ ...formData, productId, productLabel });
    };

    const handleQuantityInput = (value: string) => {
        if (/^(\d+)?$/.test(value)) {
            setFormData({ ...formData, quantity: value ? Number(value) : null });
        }
    };

    const handleExplanationInput = (value: string) => {
        setFormData({ ...formData, explanation: value });
    };

    const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
        e.preventDefault();
        const { columnId, productId, productLabel, quantity, explanation } = formData;
        if (!explanation || !columnId || !productId || quantity === null) {
            toast.error("L'un des champs n'a pas été renseigné");
            return;
        }
        const result = await createSiteInventoryStock({
            variables: { columnId, productId, quantity, explanation },
        });

        const data = result.data?.createColumnInventoryStock;
        if (!data) {
            throw new Error("Une erreur inconnue s'est produite");
        }
        if (!data.success) {
            throw new Error(`L'erreur suivante est survenue : ${data.error ?? 'inconnue'}`);
        }

        setFormData(initialFormData);
        toast.success(`Le stock du produit "${productLabel}" a bien été mis à jour !`);
    };

    return (
        <Container>
            <Header>
                <HeaderTitle>
                    <PageTitle page={PAGES.siteInventoryStockCreate} />
                </HeaderTitle>
            </Header>
            <Content>
                <Form onSubmit={handleSubmit}>
                    <ContentScrollable>
                        <Card className="bg-white">
                            <CardContent>
                                <Fields>
                                    <Info>
                                        <AiOutlineInfoCircle /> Un inventaire de stock est un changement complet de
                                        stock existant (ex: 45 au lieu de 50). Pour créer une variation de stock unique
                                        (ex: achat d'une coca):{' '}
                                        <Link to="/stock-variation/create">/stock-variation/create</Link>
                                    </Info>

                                    <ColumnSelect
                                        label="Colonne"
                                        value={formData.columnId}
                                        onChange={handleColumnSelect}
                                        isClearable
                                        dataTest="column-selector"
                                        states={SELECTABLE_COLUMN_STATES}
                                    />

                                    {formData.columnId ? (
                                        <PermissionGate
                                            requiredPermissions={[TotadminPermission.MicrostoreColumnsRead]}
                                        >
                                            <ProductWithStocksSelector
                                                productOption={formData.productId}
                                                handleProductSelect={handleProductSelect}
                                                columnId={formData.columnId}
                                            />
                                        </PermissionGate>
                                    ) : null}

                                    <TotemInput
                                        label="Quantité"
                                        placeholder="Renseigner le nombre d'unités du produit"
                                        type="number"
                                        value={formData.quantity ?? ''}
                                        onChange={handleQuantityInput}
                                        data-test="product-quantity"
                                    />
                                    <TotemInput
                                        label="Pourquoi souhaitez-vous modifier le stock ?"
                                        placeholder="Renseigner la raison de la modification de stock"
                                        type="string"
                                        value={formData.explanation ?? ''}
                                        onChange={handleExplanationInput}
                                        data-test="stock-record-explanation"
                                    />
                                </Fields>
                            </CardContent>
                        </Card>
                    </ContentScrollable>
                    <Footer>
                        <TotemPrimaryButton
                            data-test="stock-record-create_submit-button"
                            requiredPermissions={[TotadminPermission.SiteInventoriesManage]}
                        >
                            Mettre le stock à jour
                        </TotemPrimaryButton>
                    </Footer>
                </Form>
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    flex: 1;
    overflow: hidden;
`;

const Form = styled.form`
    height: 100%;
    display: flex;
    flex-direction: column;
    align-items: center;
`;

const ContentScrollable = styled.div`
    padding: 15px;
    width: 100%;
    flex: 1;
    overflow-y: auto;
`;

const Fields = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    gap: 15px;
`;

const Info = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    color: ${({ theme }) => theme.infoTextColor};
    font-size: 14px;
`;
